import React, { useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { useForm, FormProvider } from 'react-hook-form';

import { formatDateAsAnniversary } from 'helpers/dateTimes';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { useGenerateMenusSupplyOrdersMutation, useGetSuppliersQuery, TotadminPermission } from 'data/__generated__';
import { DetailFormCheckbox, DetailFormSelect } from 'components/DetailsView';
import { SiteDetailFormCheckboxes } from './components/SiteDetailFormCheckboxes';

type FormData = {
    selectedSiteIds: string[];
    selectedSupplierIds: string[];
    updateOrderProductIfPossible: boolean;
};

export function GenerateMenusSupplyOrdersButton({ date }: { date: Date }) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [generateMenusSupplyOrders, { loading }] = useGenerateMenusSupplyOrdersMutation();

    const methods = useForm<FormData>({
        defaultValues: {
            selectedSiteIds: [],
            selectedSupplierIds: [],
            updateOrderProductIfPossible: false,
        },
    });

    const {
        loading: suppliersLoading,
        data: suppliersData,
        error: suppliersError,
    } = useGetSuppliersQuery({
        fetchPolicy: 'cache-and-network',
    });

    if (!suppliersLoading && (suppliersError || !suppliersData)) {
        throw new Error('Une erreur est survenue lors de la récupération des fournisseurs');
    }

    const supplierOptions = (suppliersData?.suppliers || []).map(({ _id, name }) => ({
        label: name,
        value: _id,
    }));

    const onSubmit = methods.handleSubmit(async (formData) => {
        try {
            await generateMenusSupplyOrders({
                variables: {
                    date: formatDateAsAnniversary({ dateTime: date }),
                    siteIds: formData.selectedSiteIds,
                    supplierIds: formData.selectedSupplierIds,
                    updateOrderProductIfPossible: formData.updateOrderProductIfPossible,
                },
                onCompleted: ({ generateMenusSupplyOrders }) => {
                    if (generateMenusSupplyOrders?.success) {
                        toast.success('Les commandes fournisseurs ont bien été créées');
                        setIsOpen(false);
                    } else {
                        toast.error(
                            `Une erreur s'est produite : ${generateMenusSupplyOrders?.error || 'erreur inconnue'}`,
                        );
                    }
                },
                onError: (error) => {
                    toast.error(`Impossible de créer les commandes fournisseur : ${error}`);
                },
            });
        } catch (error) {
            toast.error(`Impossible de créer les commandes fournisseur : ${error}`);
            throw Error('Impossible de créer les commandes fournisseur');
        }
    });

    return (
        <>
            <TotemPopup
                title="Création des cartes achats"
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                contentOverflow="visible"
            >
                <FormProvider {...methods}>
                    <form onSubmit={onSubmit}>
                        <FormContent>
                            <SiteDetailFormCheckboxes label="Site(s) concerné(s)" name="selectedSiteIds" />
                            <DetailFormSelect<string>
                                isMulti
                                label="Fournisseur(s) (tous par défaut)"
                                placeholder="Sélectionnez les fournisseurs concernés"
                                isClearable
                                name="selectedSupplierIds"
                                options={supplierOptions}
                            />
                            <DetailFormCheckbox
                                label="Mettre à jour les cartes achats existantes"
                                name="updateOrderProductIfPossible"
                                defaultChecked={false}
                            />
                            <TotemPrimaryLoadingButton
                                type="submit"
                                loading={loading}
                                useIcon={false}
                                label="Confirmer"
                                requiredPermissions={[TotadminPermission.MenusManage]}
                            />
                        </FormContent>
                    </form>
                </FormProvider>
            </TotemPopup>
            <TotemPrimaryButton
                disabled={loading}
                onClick={() => setIsOpen(true)}
                requiredPermissions={[TotadminPermission.MenusManage]}
            >
                Achats
            </TotemPrimaryButton>
        </>
    );
}

const FormContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;
